import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box, Checkbox, Chip, InputAdornment, Paper, Stack,
    TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import dayjs from "dayjs";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import ResizableTable from "../../../component/common/ResizableTable";
import FormModal from "../../../component/common/FormModal";
import FormField from "../../../component/common/FormField";
import ActionButton from "../../../component/common/ActionButton";
import LoadingDots from "../../../component/common/LoadingDots";
import { vehicleApi } from "../api/vehicleApi";
import { runApiAction } from "../../../util/apiFeedback";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { formatDate } from "../../../util/dateUtils";
import { formatNumberWithCommas, formatKrw, toNumericInputState } from "../../../util/numberFormat";
import { ds } from "../../../styles/designSystem";

const C = ds.colors;

const todayStr = () => formatDate(new Date());

const toInt = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(String(v).replace(/[^0-9-]/g, ""));
    return Number.isFinite(n) ? n : null;
};

const commaInput = (raw) => toNumericInputState(raw).display;
const kmText = (v) => (v == null ? "—" : formatNumberWithCommas(v));

/** 상태별 색 — 초과는 빨강, 임박은 주황, 정상은 초록, 판정 불가는 회색. */
const STATUS_STYLE = {
    OVERDUE:  { color: C.error,     bg: C.errorBg },
    DUE_SOON: { color: C.warning,   bg: C.warningBg },
    OK:       { color: C.success,   bg: C.successBg },
    UNKNOWN:  { color: C.textMuted, bg: C.bg },
};
const statusStyle = (s) => STATUS_STYLE[s] || STATUS_STYLE.UNKNOWN;

const emptyItem = (vehicleId) => ({
    id: null, vehicleId,
    name: "", intervalKm: "", intervalMonths: "", memo: "",
    applyToAllVehicles: false, active: true,
});

const emptyRecord = (vehicleId, currentOdometer) => ({
    id: null, vehicleId,
    servicedDate: todayStr(),
    odometer: formatNumberWithCommas(currentOdometer),
    shop: "", note: "",
    selected: {},        // itemId → true
    costs: {},           // itemId → 문자열(콤마 포함)
    useCustom: false,
    customName: "",
    customCost: "",
});

/**
 * 정비 탭 — "다음 정비 언제 하지?" 와 "지난 정비 뭐 했지?" 두 질문에 답하는 화면.
 *
 * <p>위쪽은 항목별 현황 카드(상태 + 남은 거리·일수), 아래쪽은 정비 이력 표다.
 *
 * <p>다음 시점·남은 거리는 서버가 조회할 때마다 계산해 내려준다 (DB 에 없다).
 * 계기판이 운행마다 오르므로 저장해두면 그 순간부터 실제와 어긋나기 때문.
 */
export default function VehicleMaintenanceTab({ vehicle, onChanged }) {
    const vehicleId = vehicle?.id ?? null;

    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving]   = useState(false);
    const [itemForm, setItemForm]     = useState(null);
    const [recordForm, setRecordForm] = useState(null);

    const load = useCallback(() => {
        if (!vehicleId) { setData(null); return; }
        setLoading(true);
        vehicleApi.getMaintenance(vehicleId)
            .then(d => setData(d || null))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [vehicleId]);

    useEffect(() => { load(); }, [load]);

    const items    = useMemo(() => data?.items   || [], [data]);
    const records  = data?.records || [];
    const summary  = data?.summary || null;
    const currentOdometer = data?.currentOdometer ?? null;

    /** 이력 작성에서 고를 수 있는 항목 — 비활성 항목은 새로 기록할 대상이 아니다. */
    const activeItems = useMemo(() => items.filter(i => i.active), [items]);

    const reload = () => { load(); onChanged?.(); };

    // ─── 정비 항목 (주기) ────────────────────────────────────────

    const openItemNew  = () => setItemForm(emptyItem(vehicleId));
    const openItemEdit = (i) => setItemForm({
        id: i.id, vehicleId,
        name: i.name || "",
        intervalKm: formatNumberWithCommas(i.intervalKm),
        intervalMonths: i.intervalMonths != null ? String(i.intervalMonths) : "",
        memo: i.memo || "",
        active: i.active !== false,
        canEdit: Boolean(i.canEdit),
        recordCount: i.recordCount || 0,
        applyToAllVehicles: false,
    });

    // 주기는 둘 중 하나만 있어도 된다 — 거리로만 관리하는 항목과 기간으로만 관리하는
    // 항목(자동차 검사 등)이 둘 다 있기 때문. 서버도 같은 규칙으로 막는다.
    const itemValid = Boolean(itemForm)
        && !!itemForm.name?.trim()
        && (toInt(itemForm.intervalKm) != null || toInt(itemForm.intervalMonths) != null);

    const saveItem = async () => {
        const f = itemForm;
        const payload = {
            vehicleId: f.vehicleId,
            name: f.name,
            intervalKm: toInt(f.intervalKm),
            intervalMonths: toInt(f.intervalMonths),
            memo: f.memo || null,
            active: f.active,
            ...(f.id ? {} : { applyToAllVehicles: f.applyToAllVehicles }),
        };
        const res = await runApiAction({
            request: () => f.id ? vehicleApi.updateMaintenanceItem(f.id, payload)
                                : vehicleApi.createMaintenanceItem(payload),
            successMsg: f.id ? "정비 항목이 수정되었습니다." : "정비 항목이 등록되었습니다.",
            setLoading: setSaving, throwOnError: true,
        });
        if (res.ok) { setItemForm(null); reload(); }
    };

    const deleteItem = async (f) => {
        const hasRecords = (f.recordCount || 0) > 0;
        const message = hasRecords
            ? `"${f.name}" 항목에 정비 이력 ${f.recordCount}건이 있습니다.\n이력 보존을 위해 삭제 대신 '관리 안 함'으로 처리됩니다. 계속할까요?`
            : `"${f.name}" 정비 항목을 삭제하시겠습니까?`;
        if (!(await confirmDialog({ message }))) return;
        await runApiAction({
            request: () => vehicleApi.deleteMaintenanceItem(f.id),
            successMsg: hasRecords ? "관리 안 함으로 처리되었습니다." : "삭제되었습니다.",
        });
        setItemForm(null);
        reload();
    };

    // ─── 정비 이력 ───────────────────────────────────────────────

    const openRecordNew = () => setRecordForm(emptyRecord(vehicleId, currentOdometer));

    const openRecordEdit = (r) => setRecordForm({
        id: r.id, vehicleId,
        servicedDate: r.servicedDate,
        odometer: formatNumberWithCommas(r.odometer),
        shop: r.shop || "",
        note: r.note || "",
        selected: r.itemId ? { [r.itemId]: true } : {},
        costs: r.itemId ? { [r.itemId]: formatNumberWithCommas(r.cost) } : {},
        useCustom: !r.itemId,
        customName: r.itemId ? "" : (r.itemName || ""),
        customCost: r.itemId ? "" : formatNumberWithCommas(r.cost),
        canEdit: Boolean(r.canEdit),
    });

    const recordReadOnly = Boolean(recordForm?.id && !recordForm.canEdit);
    const selectedItemIds = useMemo(
        () => (recordForm ? Object.keys(recordForm.selected).filter(k => recordForm.selected[k]) : []),
        [recordForm]);

    /** 입력한 정비 계기판이 현재보다 얼마나 큰지 (정상이면 null) — 저장은 막지 않고 짚어만 준다. */
    const odometerAhead = useMemo(() => {
        if (!recordForm || currentOdometer == null) return null;
        const v = toInt(recordForm.odometer);
        return v != null && v > currentOdometer ? v - currentOdometer : null;
    }, [recordForm, currentOdometer]);

    const recordValid = Boolean(recordForm)
        && !!recordForm.servicedDate
        && toInt(recordForm.odometer) != null
        && (recordForm.useCustom
            ? !!recordForm.customName?.trim()
            : selectedItemIds.length > 0);

    const saveRecord = async () => {
        const f = recordForm;
        const payload = {
            vehicleId: f.vehicleId,
            servicedDate: f.servicedDate,
            odometer: toInt(f.odometer),
            shop: f.shop || null,
            note: f.note || null,
            ...(f.useCustom
                ? { itemIds: [], customName: f.customName, costs: [toInt(f.customCost)] }
                : {
                    itemIds: selectedItemIds.map(Number),
                    // 비용은 itemIds 와 같은 순서로 보낸다 — 서버가 인덱스로 짝을 맞춘다
                    costs: selectedItemIds.map(id => toInt(f.costs[id])),
                }),
        };
        const res = await runApiAction({
            request: () => f.id ? vehicleApi.updateMaintenanceRecord(f.id, payload)
                                : vehicleApi.createMaintenanceRecords(payload),
            successMsg: f.id ? "정비 이력이 수정되었습니다."
                             : `정비 이력이 등록되었습니다${selectedItemIds.length > 1 ? ` (${selectedItemIds.length}건)` : ""}.`,
            setLoading: setSaving, throwOnError: true,
        });
        if (res.ok) { setRecordForm(null); reload(); }
    };

    const deleteRecord = async (f) => {
        if (!(await confirmDialog({
            message: `${formatDate(f.servicedDate)} 정비 이력을 삭제하시겠습니까?\n삭제하면 이 항목의 다음 정비 시점이 이전 이력 기준으로 되돌아갑니다.`,
        }))) return;
        await runApiAction({ request: () => vehicleApi.deleteMaintenanceRecord(f.id), successMsg: "삭제되었습니다." });
        setRecordForm(null);
        reload();
    };

    // ─── 렌더 ────────────────────────────────────────────────────

    if (!vehicle) return null;
    if (loading) return <Paper sx={{ ...ds.cardPadded, p: 2 }}><LoadingDots /></Paper>;

    return (
        <Stack gap={2}>
            {/* ─── 정비 현황 ──────────────────────────────────── */}
            <Paper sx={{ ...ds.cardPadded, p: 2 }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={1.5}>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>정비 현황</Typography>
                    <Chip size="small" label={`현재 ${kmText(currentOdometer)} km`}
                        sx={{ height: 22, fontSize: 11.5, fontWeight: 700,
                            bgcolor: C.primaryBg, color: C.primaryText }} />
                    <Box sx={{ flex: 1 }} />
                    {summary && <MaintenanceSummary summary={summary} />}
                    <ActionButton variant="secondary" startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={openItemNew} label="정비 항목 추가" sx={{ fontSize: 11.5, ml: 0.5 }} />
                </Stack>

                {items.length === 0 ? (
                    <Stack alignItems="center" gap={0.75} sx={{ py: 4 }}>
                        <BuildRoundedIcon sx={{ fontSize: 28, color: C.textMuted }} />
                        <Typography sx={{ fontSize: 13, color: C.textMuted }}>
                            등록된 정비 항목이 없습니다.
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: C.textMuted, textAlign: "center", maxWidth: 460 }}>
                            “엔진오일 매 10,000km” 처럼 주기를 등록하면 다음 정비 시점을 계산해 알려줍니다.
                            계산은 정비 이력을 기준으로 하니, 지난 정비가 있다면 “정비 기록”으로 함께 넣어 주세요.
                        </Typography>
                    </Stack>
                ) : (
                    <Box sx={{
                        display: "grid", gap: 1.25,
                        gridTemplateColumns: {
                            xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(4, 1fr)",
                        },
                    }}>
                        {items.map(i => (
                            <MaintenanceItemCard key={i.id} item={i} currentOdometer={currentOdometer}
                                onEdit={() => openItemEdit(i)} />
                        ))}
                    </Box>
                )}
            </Paper>

            {/* ─── 정비 이력 ──────────────────────────────────── */}
            <Paper sx={{ ...ds.cardPadded, p: 2, overflowX: "auto" }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={1.5}>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>정비 이력</Typography>
                    <Box sx={{ flex: 1 }} />
                    <ActionButton variant="add" startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={openRecordNew} label="정비 기록" />
                </Stack>

                <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell>정비일</TableCell>
                            <TableCell>항목</TableCell>
                            <TableCell>계기판 (km)</TableCell>
                            <TableCell>비용</TableCell>
                            <TableCell>업체</TableCell>
                            <TableCell>메모</TableCell>
                            <TableCell>작성자</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {records.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} sx={{ py: 5, color: C.textMuted, fontSize: 13 }}>
                                    등록된 정비 이력이 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : records.map(r => (
                            <RecordRow key={r.id} row={r} onOpen={() => openRecordEdit(r)} />
                        ))}
                    </TableBody>
                </ResizableTable>
                {records.length > 0 && (
                    <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 1 }}>
                        행을 클릭하면 상세를 열어 수정·삭제할 수 있습니다.
                    </Typography>
                )}
            </Paper>

            {/* ─── 정비 항목 모달 ─────────────────────────────── */}
            <FormModal
                open={Boolean(itemForm)}
                onClose={() => setItemForm(null)}
                title={itemForm?.id ? "정비 항목 수정" : "정비 항목 추가"}
                subtitle={vehicle ? `${vehicle.name} · ${vehicle.plateNo}` : ""}
                maxWidth="sm"
                onSave={saveItem}
                saving={saving}
                saveDisabled={!itemValid}
                footerExtras={itemForm?.id && itemForm?.canEdit ? (
                    <ActionButton variant="delete" onClick={() => deleteItem(itemForm)} />
                ) : null}
            >
                {itemForm && (
                    <Stack gap={0.5}>
                        <SectionLabel>항목</SectionLabel>
                        <FormField label="항목명" required>
                            <TextField fullWidth size="small" value={itemForm.name}
                                placeholder="예: 엔진오일, 에어컨필터, 타이어"
                                onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))} />
                        </FormField>

                        {/* 새로 만들 때만 — 항목은 차량별로 두는 구조라 이게 없으면
                            같은 입력을 차량 수만큼 반복해야 한다 */}
                        {!itemForm.id && (
                            <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.5, mb: 0.5 }}>
                                <Checkbox size="small" sx={{ p: 0.5 }}
                                    checked={itemForm.applyToAllVehicles}
                                    onChange={e => setItemForm(f => ({ ...f, applyToAllVehicles: e.target.checked }))} />
                                <Typography sx={{ fontSize: 12, color: C.textSecondary }}>
                                    운행 중인 모든 차량에 함께 추가
                                    <Box component="span" sx={{ color: C.textMuted, ml: 0.5 }}>
                                        (아래 마지막 정비 기록은 이 차량에만 들어갑니다)
                                    </Box>
                                </Typography>
                            </Stack>
                        )}

                        <SectionLabel>주기</SectionLabel>
                        <FormRow>
                            <FormField label="주기 (km)" sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={itemForm.intervalKm}
                                    inputProps={{ inputMode: "numeric" }}
                                    placeholder="예: 10,000"
                                    InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                                    onChange={e => setItemForm(f => ({ ...f, intervalKm: commaInput(e.target.value) }))} />
                            </FormField>
                            <FormField label="주기 (개월)" sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={itemForm.intervalMonths}
                                    inputProps={{ inputMode: "numeric" }}
                                    placeholder="예: 12"
                                    InputProps={{ endAdornment: <InputAdornment position="end">개월</InputAdornment> }}
                                    onChange={e => setItemForm(f => ({ ...f, intervalMonths: commaInput(e.target.value) }))} />
                            </FormField>
                        </FormRow>
                        <Typography sx={{ fontSize: 10.5, color: C.textMuted, mb: 0.5 }}>
                            둘 중 하나만 넣어도 됩니다. 둘 다 넣으면 <b>먼저 오는 쪽</b>이 정비 시점이 됩니다.
                        </Typography>

                        {/* 마지막 정비 시점은 여기서 받지 않는다 — 그건 '정비 이력' 이 답하는 값이고,
                            같은 사실을 두 군데 적게 하면 둘이 어긋났을 때 뭐가 맞는지 알 수 없다.
                            지난 정비는 이력으로 넣으면 되고, 그 편이 언제·얼마에 했는지까지 남는다. */}
                        {!itemForm.id && (
                            <Typography sx={{ fontSize: 10.5, color: C.textMuted, mb: 0.5 }}>
                                다음 시점은 <b>정비 이력</b>을 기준으로 계산합니다. 이력이 없는 동안은 “확인 필요”로
                                표시되니, 지난 정비가 있다면 “정비 기록”으로 먼저 넣어 주세요.
                            </Typography>
                        )}

                        <SectionLabel>기타</SectionLabel>
                        <FormField label="메모">
                            <TextField fullWidth size="small" multiline minRows={2} value={itemForm.memo}
                                placeholder="규격·주의사항 등 (예: 5W30 합성유)"
                                onChange={e => setItemForm(f => ({ ...f, memo: e.target.value }))} />
                        </FormField>
                    </Stack>
                )}
            </FormModal>

            {/* ─── 정비 이력 모달 ─────────────────────────────── */}
            <FormModal
                open={Boolean(recordForm)}
                onClose={() => setRecordForm(null)}
                title={!recordForm?.id ? "정비 기록" : recordReadOnly ? "정비 이력" : "정비 이력 수정"}
                subtitle={vehicle ? `${vehicle.name} · ${vehicle.plateNo}` : ""}
                maxWidth="sm"
                onSave={recordReadOnly ? undefined : saveRecord}
                saving={saving}
                saveDisabled={!recordValid}
                cancelLabel={recordReadOnly ? "닫기" : "취소"}
                footerExtras={recordForm?.id && !recordReadOnly ? (
                    <ActionButton variant="delete" onClick={() => deleteRecord(recordForm)} />
                ) : null}
            >
                {recordForm && (
                    <Stack gap={0.5}>
                        {recordReadOnly && (
                            <Stack direction="row" gap={0.75} alignItems="flex-start" sx={{
                                px: 1.2, py: 0.9, borderRadius: 1, mb: 0.5,
                                bgcolor: C.warningBg, border: `1px solid ${C.warning}`,
                            }}>
                                <WarningAmberRoundedIcon sx={{ fontSize: 15, color: C.warning, mt: 0.1 }} />
                                <Typography sx={{ fontSize: 11.5, color: C.warning, lineHeight: 1.5 }}>
                                    본인이 작성한 기록이 아니어서 열람만 할 수 있습니다.
                                </Typography>
                            </Stack>
                        )}

                        <SectionLabel>정비</SectionLabel>
                        <FormRow>
                            <FormField label="정비 일자" required sx={FIELD_SX}>
                                <TextField fullWidth size="small" type="date" value={recordForm.servicedDate}
                                    disabled={recordReadOnly}
                                    inputProps={{ max: todayStr() }}
                                    onChange={e => setRecordForm(f => ({ ...f, servicedDate: e.target.value }))} />
                            </FormField>
                            <FormField label="정비 시 계기판" required sx={FIELD_SX}>
                                <TextField fullWidth size="small" value={recordForm.odometer}
                                    disabled={recordReadOnly}
                                    inputProps={{ inputMode: "numeric" }}
                                    helperText={currentOdometer != null ? `현재 ${kmText(currentOdometer)}km` : ""}
                                    FormHelperTextProps={{ sx: { fontSize: 10.5, mx: 0, mt: 0.4, color: C.textMuted } }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                                    onChange={e => setRecordForm(f => ({ ...f, odometer: commaInput(e.target.value) }))} />
                            </FormField>
                        </FormRow>

                        {/* 막지는 않는다 — 지난 정비를 뒤늦게 넣는데 운행 기록이 아직 없어
                            계기판이 안 올라온 경우가 실제로 있다. 대신 저장 전에 한 번 짚어준다.
                            (운행일지의 '계기판 불일치' 와 같은 방식) */}
                        {odometerAhead != null && (
                            <Stack direction="row" gap={0.75} alignItems="flex-start" sx={{
                            }}>
                                <Typography sx={{ fontSize: 11.5, color: C.warning, lineHeight: 1.5 }}>
                                    현재 계기판({kmText(currentOdometer)}km)보다 {formatNumberWithCommas(odometerAhead)}km 큽니다.
                                    운행일지 작성 후 정비기록을 진행해주세요
                                </Typography>
                            </Stack>
                        )}

                        {/* 항목을 여러 개 고르면 서버가 항목 수만큼 이력을 만든다 (1행 = 1항목).
                            정비소에 한 번 가서 서너 개를 함께 가는 게 보통이라 한 번에 담게 한다.
                            비용을 항목마다 받는 건 명세서에 항목별 금액이 찍혀 나오기 때문. */}
                        <SectionLabel>정비 항목</SectionLabel>
                        {activeItems.length === 0 && !recordForm.useCustom ? (
                            <Typography sx={{ fontSize: 11.5, color: C.textMuted, mb: 0.5 }}>
                                등록된 정비 항목이 없습니다. 아래 “목록에 없는 정비”로 기록하거나,
                                먼저 정비 항목을 추가하세요.
                            </Typography>
                        ) : (
                            <Stack gap={0.25} sx={{
                                border: `1px solid ${C.border}`, borderRadius: 1, p: 0.75,
                                opacity: recordForm.useCustom ? 0.45 : 1,
                                pointerEvents: recordForm.useCustom ? "none" : "auto",
                                maxHeight: 260, overflowY: "auto",
                            }}>
                                {activeItems.map(i => {
                                    const checked = Boolean(recordForm.selected[i.id]);
                                    return (
                                        <Stack key={i.id} direction="row" alignItems="center" gap={0.5}>
                                            <Checkbox size="small" sx={{ p: 0.5 }} checked={checked}
                                                disabled={recordReadOnly}
                                                onChange={e => setRecordForm(f => ({
                                                    ...f,
                                                    selected: { ...f.selected, [i.id]: e.target.checked },
                                                }))} />
                                            <Typography sx={{
                                                fontSize: 12.5, fontWeight: checked ? 700 : 500,
                                                color: checked ? C.textPrimary : C.textSecondary,
                                                flex: 1, minWidth: 0,
                                            }}>
                                                {i.name}
                                                <Box component="span" sx={{ fontSize: 10.5, color: C.textMuted, ml: 0.75 }}>
                                                    {intervalText(i)}
                                                </Box>
                                            </Typography>
                                            {checked && (
                                                <TextField size="small" value={recordForm.costs[i.id] || ""}
                                                    disabled={recordReadOnly}
                                                    placeholder="비용"
                                                    inputProps={{ inputMode: "numeric", style: { textAlign: "right" } }}
                                                    InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                                                    sx={{ width: 150, flexShrink: 0 }}
                                                    onChange={e => setRecordForm(f => ({
                                                        ...f,
                                                        costs: { ...f.costs, [i.id]: commaInput(e.target.value) },
                                                    }))} />
                                            )}
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        )}

                        {/* 주기 관리 대상이 아닌 정비(범퍼 교체·사고 수리 등)도 이력에는 남아야 한다 */}
                        <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                            <Checkbox size="small" sx={{ p: 0.5 }} checked={recordForm.useCustom}
                                disabled={recordReadOnly}
                                onChange={e => setRecordForm(f => ({ ...f, useCustom: e.target.checked }))} />
                            <Typography sx={{ fontSize: 12, color: C.textSecondary }}>
                                목록에 없는 정비 (범퍼 교체·사고 수리 등)
                            </Typography>
                        </Stack>
                        {recordForm.useCustom && (
                            <FormRow>
                                <FormField label="정비 내용" required sx={FIELD_SX}>
                                    <TextField fullWidth size="small" value={recordForm.customName}
                                        disabled={recordReadOnly}
                                        placeholder="예: 앞범퍼 교체"
                                        onChange={e => setRecordForm(f => ({ ...f, customName: e.target.value }))} />
                                </FormField>
                                <FormField label="비용" sx={FIELD_SX}>
                                    <TextField fullWidth size="small" value={recordForm.customCost}
                                        disabled={recordReadOnly}
                                        inputProps={{ inputMode: "numeric" }}
                                        InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                                        onChange={e => setRecordForm(f => ({ ...f, customCost: commaInput(e.target.value) }))} />
                                </FormField>
                            </FormRow>
                        )}
                        {!recordForm.useCustom && !recordForm.id && selectedItemIds.length > 1 && (
                            <Typography sx={{ fontSize: 10.5, color: C.textMuted, mt: 0.3 }}>
                                {selectedItemIds.length}건의 정비 이력이 만들어집니다 (항목마다 한 건).
                            </Typography>
                        )}

                        <SectionLabel>기타</SectionLabel>
                        <FormField label="정비 업체">
                            <TextField fullWidth size="small" value={recordForm.shop}
                                disabled={recordReadOnly}
                                placeholder="예: 삼성 오토큐"
                                onChange={e => setRecordForm(f => ({ ...f, shop: e.target.value }))} />
                        </FormField>
                        <FormField label="메모">
                            <TextField fullWidth size="small" multiline minRows={2} value={recordForm.note}
                                disabled={recordReadOnly}
                                placeholder="교체 부품·다음 점검 안내 등"
                                onChange={e => setRecordForm(f => ({ ...f, note: e.target.value }))} />
                        </FormField>
                    </Stack>
                )}
            </FormModal>
        </Stack>
    );
}

/* ─── 폼 레이아웃 (운행 기록 폼과 같은 2열 그리드) ─────────────── */

const FORM_ROW_SX = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: 1.5,
    alignItems: "start",
};
const FIELD_SX = { minWidth: 0, width: "100%" };

function FormRow({ children }) {
    return <Box sx={FORM_ROW_SX}>{children}</Box>;
}

function SectionLabel({ children }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 800, color: C.textMuted, letterSpacing: "0.02em",
            mt: 1.2, mb: 0.3, pb: 0.4, borderBottom: `1px solid ${C.borderLight}`,
        }}>
            {children}
        </Typography>
    );
}

/** "10,000km · 12개월" 같은 주기 요약. */
function intervalText(i) {
    const parts = [];
    if (i.intervalKm != null) parts.push(`${formatNumberWithCommas(i.intervalKm)}km`);
    if (i.intervalMonths != null) parts.push(`${i.intervalMonths}개월`);
    return parts.length ? `매 ${parts.join(" · ")}` : "";
}

/* ─── 항목 현황 카드 ─────────────────────────────────────────── */

/**
 * 항목 하나의 현황 — 상태 칩 + 남은 거리/일수.
 *
 * <p>한때 진행 막대를 뒀다가 걷어냈다. 막대는 100%에서 멈춰서 1,000km 지난 항목과
 * 30,000km 지난 항목이 똑같이 꽉 찬 모습으로 보였다 — 정작 급한 정도를 구분해야 하는
 * 구간에서 정보를 잃는 셈이라, 그 자리를 숫자와 색에 맡긴다.
 */
function MaintenanceItemCard({ item: i, currentOdometer, onEdit }) {
    const st = statusStyle(i.status);
    const ahead = currentOdometer != null && i.lastOdometer != null && i.lastOdometer > currentOdometer;

    return (
        <Paper sx={{
            ...ds.card, p: 1.25,
            border: `1px solid ${i.status === "OK" || i.status === "UNKNOWN" ? C.border : st.color}`,
            bgcolor: i.status === "OVERDUE" ? st.bg : "#fff",
            opacity: i.active === false ? 0.5 : 1,
        }}>
            <Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, flex: 1, minWidth: 0,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {i.name}
                </Typography>
                {i.active === false && (
                    <Chip label="관리 안 함" size="small"
                        sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: C.borderLight, color: C.textMuted }} />
                )}
                <Chip label={i.statusLabel} size="small" sx={{
                    height: 17, fontSize: 9.5, fontWeight: 800,
                    bgcolor: st.bg, color: st.color, border: `1px solid ${st.color}`,
                }} />
                {/* 수정은 전 직원에게 연다 — 주기는 여러 사람의 경험으로 다듬어지는 공용 값이라,
                    처음 등록한 사람만 고칠 수 있으면 틀린 주기가 그대로 굳는다.
                    (서버도 수정에는 권한을 걸지 않는다. 삭제만 작성자·VEHICLE_MANAGE 로 좁힌다.) */}
                <Tooltip title="정비 항목 수정">
                    <Box component="button" onClick={onEdit} aria-label="정비 항목 수정" sx={{
                        border: 0, bgcolor: "transparent", cursor: "pointer", p: 0.25, lineHeight: 0,
                    }}>
                        <EditRoundedIcon sx={{ fontSize: 13, color: C.textMuted }} />
                    </Box>
                </Tooltip>
            </Stack>

            <Typography sx={{ fontSize: 12, fontWeight: 800, color: st.color, lineHeight: 1.4 }}>
                {remainingText(i)}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: C.textMuted, lineHeight: 1.5, mt: 0.15 }}>
                {intervalText(i)}
                {i.nextOdometer != null && ` · 다음 ${formatNumberWithCommas(i.nextOdometer)}km`}
                {i.nextDate && ` · ${dayjs(i.nextDate).format("YY.M.D")}`}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: C.textMuted, lineHeight: 1.5 }}>
                {i.lastOdometer != null || i.lastDate
                    ? `마지막 ${i.lastOdometer != null ? `${formatNumberWithCommas(i.lastOdometer)}km` : ""}`
                      + `${i.lastDate ? ` · ${dayjs(i.lastDate).format("YY.M.D")}` : ""}`
                    : "정비 이력이 없습니다"}
            </Typography>

            {/* 마지막 정비의 계기판이 현재보다 크면 계산이 통째로 어긋난다 (자릿수 오기입).
                이 경우 남은 거리가 크게 나와 '정상' 으로 보이므로, 조용히 넘어가면 안 된다. */}
            {ahead && (
                <Stack direction="row" alignItems="center" gap={0.4} sx={{ mt: 0.5 }}>
                    <WarningAmberRoundedIcon sx={{ fontSize: 12, color: C.warning, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 10, color: C.warning, lineHeight: 1.4 }}>
                        마지막 정비 계기판이 현재({kmText(currentOdometer)}km)보다 큽니다 — 이력을 확인하세요
                    </Typography>
                </Stack>
            )}
        </Paper>
    );
}

/** 카드의 핵심 한 줄 — 얼마나 남았나 / 얼마나 지났나. */
function remainingText(i) {
    if (i.status === "UNKNOWN") return "정비 이력을 등록하세요";

    const bits = [];
    if (i.remainingKm != null) {
        bits.push(i.remainingKm <= 0
            ? `${formatNumberWithCommas(Math.abs(i.remainingKm))}km 지남`
            : `${formatNumberWithCommas(i.remainingKm)}km 남음`);
    }
    if (i.remainingDays != null) {
        bits.push(i.remainingDays <= 0
            ? `${formatNumberWithCommas(Math.abs(i.remainingDays))}일 지남`
            : `${formatNumberWithCommas(i.remainingDays)}일 남음`);
    }
    return bits.length ? bits.join(" · ") : "—";
}

/* ─── 요약 ───────────────────────────────────────────────────── */

function MaintenanceSummary({ summary: s }) {
    return (
        <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
            <SummaryStat label="항목" value={`${s.itemCount}건`} />
            {s.overdueCount > 0 && <SummaryStat label="초과" value={`${s.overdueCount}건`} tone="error" />}
            {s.dueSoonCount > 0 && <SummaryStat label="임박" value={`${s.dueSoonCount}건`} tone="warning" />}
            {/* 판정을 못 한 항목은 조용한 게 아니라 '모르는' 것이라 따로 세어 보여준다 */}
            {s.unknownCount > 0 && <SummaryStat label="확인 필요" value={`${s.unknownCount}건`} />}
            <SummaryStat label="정비 비용" value={formatKrw(s.totalCost)} />
        </Stack>
    );
}

function SummaryStat({ label, value, tone = "neutral" }) {
    const color  = tone === "error" ? C.error : tone === "warning" ? C.warning : C.textMuted;
    const valueC = tone === "neutral" ? C.textPrimary : color;
    const bg     = tone === "error" ? C.errorBg : tone === "warning" ? C.warningBg : C.bg;
    const border = tone === "neutral" ? C.border : color;
    return (
        <Stack direction="row" alignItems="baseline" gap={0.5} sx={{
            px: 1, py: 0.35, borderRadius: 0.75, bgcolor: bg, border: `1px solid ${border}`,
        }}>
            <Typography sx={{ fontSize: 10.5, color, fontWeight: 700 }}>{label}</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: valueC, fontVariantNumeric: "tabular-nums" }}>
                {value}
            </Typography>
        </Stack>
    );
}

/* ─── 이력 행 ────────────────────────────────────────────────── */

function RecordRow({ row: r, onOpen }) {
    return (
        <TableRow hover onClick={onOpen} sx={{ cursor: "pointer" }}>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{dayjs(r.servicedDate).format("YY.M.D (ddd)")}</TableCell>
            <TableCell>
                <Stack direction="row" alignItems="center" justifyContent="center" gap={0.4}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{r.itemName}</Typography>
                    {/* 주기 관리 대상이 아닌 건 — 다음 시점 계산에 안 들어간다는 걸 표에서도 알려준다 */}
                    {r.oneOff && (
                        <Chip label="일회성" size="small" sx={{
                            height: 16, fontSize: 9, fontWeight: 700,
                            bgcolor: C.borderLight, color: C.textMuted,
                        }} />
                    )}
                </Stack>
            </TableCell>
            <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.4}>
                    {/* 현재 계기판을 넘은 이력 — 자릿수를 잘못 치면 그 항목이 영영 '정상' 으로 보인다 */}
                    {r.odometerOverCurrent != null && (
                        <Tooltip title={`차량의 현재 계기판보다 ${formatNumberWithCommas(r.odometerOverCurrent)}km 큽니다. 자릿수를 잘못 입력했다면 이 항목의 다음 정비 시점이 실제보다 훨씬 뒤로 밀립니다.`}>
                            <WarningAmberRoundedIcon sx={{ fontSize: 14, color: C.warning }} />
                        </Tooltip>
                    )}
                    <span>{kmText(r.odometer)}</span>
                </Stack>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
                {r.cost != null ? formatKrw(r.cost) : <Box component="span" sx={{ color: C.textMuted, fontWeight: 400 }}>—</Box>}
            </TableCell>
            <TableCell>{r.shop || "—"}</TableCell>
            <TableCell align="left">
                {r.note ? (
                    <Tooltip title={r.note}>
                        <Typography sx={{
                            fontSize: 12, color: C.textSecondary, maxWidth: 240,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                            {r.note}
                        </Typography>
                    </Tooltip>
                ) : <Typography sx={{ fontSize: 12, color: C.textMuted }}>—</Typography>}
            </TableCell>
            <TableCell sx={{ color: C.textSecondary, fontSize: 12 }}>{r.createdByName || "—"}</TableCell>
        </TableRow>
    );
}
